import { useEffect } from 'react';
import analyticsService from '@/services/analytics.service';

// ─── Env-var resolution ───────────────────────────────────────────────────────

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const FB_ID = import.meta.env.VITE_FB_PIXEL_ID;
const TIKTOK_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID;
const HOTJAR_ID = import.meta.env.VITE_HOTJAR_ID;
const GTM_ID = import.meta.env.VITE_GTM_ID;
const CLARITY_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;
const PINTEREST_ID = import.meta.env.VITE_PINTEREST_TAG_ID;
const LINKEDIN_ID = import.meta.env.VITE_LINKEDIN_PARTNER_ID;
const KLAVIYO_ID = import.meta.env.VITE_KLAVIYO_ID;
const ZENDESK_KEY = import.meta.env.VITE_ZENDESK_KEY;

/** Returns true if the value looks like a real ID, not a placeholder. */
function isRealId(value: string | undefined): value is string {
  if (!value) return false;
  const v = value.trim();
  if (v.length < 4) return false;
  // Reject literal placeholder strings
  const placeholders = ['GA_MEASUREMENT_ID', 'FB_PIXEL_ID', 'TIKTOK_PIXEL_ID', 'HOTJAR_ID',
    'GTM-XXXXXXX', 'GTM_XXXXXXX', 'CLARITY_PROJECT_ID', 'PINTEREST_TAG_ID',
    'LINKEDIN_PARTNER_ID', 'KLAVIYO_ID', 'ZENDESK_KEY', 'YOUR_', 'REPLACE_',
    'XXXXXXXX', 'XXXXXXX'];
  return !placeholders.some(p => v.toUpperCase().includes(p));
}

// ─── Script fragments ─────────────────────────────────────────────────────────

function ga4Script(gaId: string) {
  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaId}', {
      send_page_view: false,
      cookie_flags: 'SameSite=None;Secure',
      custom_map: {
        custom_parameter_1: 'skin_type',
        custom_parameter_2: 'skin_concern',
      }
    });
  `;
}

function fbPixelScript(fbId: string) {
  return `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${fbId}');
    fbq('track', 'PageView');
  `;
}

function tiktokScript(ttId: string) {
  return `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
      ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],
      ttq.setAndDefer=function(t,t){for(var e=0;e<ttq.methods.length;e++)
      ttq.load=function(t,e){var n="https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)
      ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load(t),ttq._i=ttq._i||{},ttq._i[t]=[]
      };
      ttq.load('${ttId}');
      ttq.page();
    }(window, document, 'ttq');
  `;
}

function hotjarScript(hjId: string) {
  return `
    (function(h,o,t,j,a,r){
      h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
      h._hjSettings={hjid:${hjId},hjsv:6};
      a=o.getElementsByTagName('head')[0];
      r=o.createElement('script');r.async=1;
      r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
      a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
  `;
}

function gtmScript(gtmId: string) {
  return `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
}

function clarityScript(clarityId: string) {
  return `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${clarityId}");
  `;
}

function pinterestScript(pinId: string) {
  return `
    !function(e){if(!window.pintrk){window.pintrk=function()
    {window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var
    n=window.pintrk;n.queue=[],n.version="3.0";var
    t=document.createElement("script");t.async=!0,t.src=e;var
    r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r)}}
    ("https://s.pinimg.com/ct/core.js");
    pintrk('load', '${pinId}');
    pintrk('page');
  `;
}

function linkedinScript(liId: string) {
  return `
    _linkedin_partner_id = "${liId}";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);
  `;
}

function linkedinLoaderScript() {
  return `
    (function(l) {
      if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
      window.lintrk.q=[]}
      var s = document.getElementsByTagName("script")[0];
      var b = document.createElement("script");
      b.type = "text/javascript";b.async = true;
      b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b, s);
    })(window.lintrk);
  `;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AnalyticsScripts() {
  useEffect(() => {
    analyticsService.initialize();
  }, []);

  return (
    <>
      {/* Google Analytics 4 */}
      {isRealId(GA_ID) && (
        <>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`} />
          <script dangerouslySetInnerHTML={{ __html: ga4Script(GA_ID) }} />
        </>
      )}

      {/* Facebook Pixel */}
      {isRealId(FB_ID) && (
        <>
          <script dangerouslySetInnerHTML={{ __html: fbPixelScript(FB_ID) }} />
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src={`https://www.facebook.com/tr?id=${FB_ID}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        </>
      )}

      {/* TikTok Pixel */}
      {isRealId(TIKTOK_ID) && (
        <script dangerouslySetInnerHTML={{ __html: tiktokScript(TIKTOK_ID) }} />
      )}

      {/* Hotjar */}
      {isRealId(HOTJAR_ID) && (
        <script dangerouslySetInnerHTML={{ __html: hotjarScript(HOTJAR_ID) }} />
      )}

      {/* Google Tag Manager */}
      {isRealId(GTM_ID) && (
        <>
          <script dangerouslySetInnerHTML={{ __html: gtmScript(GTM_ID) }} />
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
              title="GTM"
            />
          </noscript>
        </>
      )}

      {/* Microsoft Clarity (Heatmaps) */}
      {isRealId(CLARITY_ID) && (
        <script dangerouslySetInnerHTML={{ __html: clarityScript(CLARITY_ID) }} />
      )}

      {/* Pinterest Tag */}
      {isRealId(PINTEREST_ID) && (
        <>
          <script dangerouslySetInnerHTML={{ __html: pinterestScript(PINTEREST_ID) }} />
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src={`https://ct.pinterest.com/v3/?event=init&tid=${PINTEREST_ID}&noscript=1`}
              alt=""
            />
          </noscript>
        </>
      )}

      {/* LinkedIn Insight Tag */}
      {isRealId(LINKEDIN_ID) && (
        <>
          <script dangerouslySetInnerHTML={{ __html: linkedinScript(LINKEDIN_ID) }} />
          <script dangerouslySetInnerHTML={{ __html: linkedinLoaderScript() }} />
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src={`https://px.ads.linkedin.com/collect/?pid=${LINKEDIN_ID}&fmt=gif`}
              alt=""
            />
          </noscript>
        </>
      )}

      {/* Klaviyo (Email Marketing) */}
      {isRealId(KLAVIYO_ID) && (
        <script
          async
          src={`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${KLAVIYO_ID}`}
        />
      )}

      {/* Zendesk Chat */}
      {isRealId(ZENDESK_KEY) && (
        <script
          id="ze-snippet"
          src={`https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`}
        />
      )}
    </>
  );
}

// E-commerce specific event tracking
export function ProductViewTracker({ product }: { product: any }) {
  useEffect(() => {
    analyticsService.viewItem({
      id: product.id,
      name: product.name,
      price: product.price,
      category: product.category,
    });
  }, [product]);

  return null;
}

export function AddToCartTracker({ 
  product, 
  quantity 
}: { 
  product: any; 
  quantity: number;
}) {
  useEffect(() => {
    analyticsService.addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      category: product.category,
    }, quantity);
  }, [product, quantity]);

  return null;
}

export function PurchaseTracker({
  transactionId,
  items,
  value,
  shipping,
  tax,
}: {
  transactionId: string;
  items: any[];
  value: number;
  shipping: number;
  tax: number;
}) {
  useEffect(() => {
    analyticsService.purchase(transactionId, items, value, shipping, tax);
  }, [transactionId, items, value, shipping, tax]);

  return null;
}

export default AnalyticsScripts;
